import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons/faQuestionCircle';
import { Button, Divider, Dropdown, FlexGrid, Header, Modal, Text, Tooltip } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generateTimeSpottedList, localyticsEvent, noop, pluralizeIfNeeded, uniqueReducer } from '../../../lib/utils';
import getDisplayableFuels from '../../../lib/utils/getDisplayableFuels';
import ANALYTICS_EVENTS from '../../constants/analyticsEvents';
import FuelPropType from '../../prop-types/fuel';
import StationPropTypes from '../../prop-types/station';
import EditMembernameModal from '../EditMembernameModal';
import { USERNAME_STATE } from '../EditMembernameStatus';
import GasPriceEditor from '../GasPriceEditor';
import StationInfoBox from '../StationInfoBox';
import ThankYou from '../ThankYou';
import styles from './StationPriceReport.module.css';

const cx = classnames.bind(styles);
const TIME_SPOTTED_LIST = generateTimeSpottedList();

export default function StationPriceReport({
  station,
  fuels,
  isLoggedIn,
  isPosting,
  fuelsPosted,
  onReportDone,
  pointsAwarded,
  postError,
  submitPriceReport,
  memberName,
  canSetMemberName,
  resetPriceReportStatus,
}) {
  const [isReportingPrices, setIsReportingPrices] = useState(true);
  const [isUsernameModalOpen, setIsUsernameModalOpen] = useState(false);
  const [isMainModalOpen, setIsMainModalOpen] = useState(false);
  const [pricesConfirmed, setPricesConfirmed] = useState({ cash: {}, credit: {} });
  const [priceWarnings, setPriceWarnings] = useState({ cash: {}, credit: {} });
  const [timeSpotted, setTimeSpotted] = useState(TIME_SPOTTED_LIST[0].value);

  const hasBothPriceTypes = fuels.some(fuel => fuel.prices.length === 2);

  const existingPrices = useMemo(() => {
    const existingPriceObj = { cash: {}, credit: {} };
    fuels.forEach((fuel) => {
      fuel.prices.forEach((priceObj) => {
        existingPriceObj[priceObj.isCash ? 'cash' : 'credit'][fuel.fuelType] = priceObj.price;
      });
    });

    return existingPriceObj;
  }, [fuels]);

  const isUSStation = useMemo(() => station.address.country === 'US', [station.address.country]);

  useEffect(() => {
    setIsReportingPrices(!fuelsPosted);
  }, [fuelsPosted]);

  const openModal = useCallback(() => {
    localyticsEvent(ANALYTICS_EVENTS.PRICE_REPORT_MODAL_OPENED, {
      Station_ID: station.id,
      Station_Name: station.name,
    });
    setIsMainModalOpen(true);
  }, [station]);

  const closeModal = useCallback(() => {
    if (isReportingPrices) {
      localyticsEvent(ANALYTICS_EVENTS.PRICE_REPORT_MODAL_ABANDONED, {
        Station_ID: station.id,
      });
    }

    if (fuelsPosted) {
      onReportDone();
    }

    setPricesConfirmed({ cash: {}, credit: {} });
    setPriceWarnings({ cash: {}, credit: {} });
    setIsUsernameModalOpen(false);
    setIsMainModalOpen(false);
    setIsReportingPrices(true);
    resetPriceReportStatus();
  }, [fuelsPosted, isReportingPrices, onReportDone, resetPriceReportStatus, station.id]);

  const handleTimeSpottedChanged = useCallback(({ target }) => {
    setTimeSpotted(target.value);
  }, []);

  const handlePricesPosted = useCallback(() => {
    const typesReported = fuels.map(fuel => fuel.displayName).reduce(uniqueReducer, []);

    const typesConfirmed = Object.entries(pricesConfirmed)
      .map(([, v]) => Object.keys(v))
      .reduce((types, type) => types.concat(type), [])
      .reduce(uniqueReducer, []);

    localyticsEvent(ANALYTICS_EVENTS.PRICE_REPORT_SUBMITTED, {
      Station_ID: station.id,
      Reported_Fuel_Products: typesReported.join(','),
      Confirmed_Fuel_Products: typesConfirmed.join(','),
    });

    submitPriceReport(station.id, {
      report: pricesConfirmed,
      timeStamp: timeSpotted,
    });
  }, [fuels, pricesConfirmed, station.id, submitPriceReport, timeSpotted]);

  const handlePricesSubmitted = useCallback(() => {
    if (canSetMemberName) {
      // we want to potentially get the user's new username before posting these prices
      setIsReportingPrices(false);
    } else {
      handlePricesPosted();
    }
  }, [canSetMemberName, handlePricesPosted]);

  const handleThankYouModalDone = useCallback(() => {
    // Since a user can decline changing their username, we may still need to post the prices
    if (!fuelsPosted) {
      handlePricesPosted();
    } else {
      closeModal();
    }
  }, [closeModal, fuelsPosted, handlePricesPosted]);

  const handleEditMemberName = useCallback(() => {
    localyticsEvent(ANALYTICS_EVENTS.CHANGE_USERNAME_OPENED, {
      Context: 'Price_Report',
    });

    setIsMainModalOpen(false);
    setIsUsernameModalOpen(true);
  }, []);

  const updateConfirmedPrices = useCallback((fuelType, priceType, value) => {
    const newPriceObject = { ...pricesConfirmed };
    const newPrice = parseFloat(value);

    if (Number.isNaN(newPrice)) {
      delete newPriceObject[priceType][fuelType];
    } else {
      newPriceObject[priceType][fuelType] = newPrice;
    }

    setPricesConfirmed(newPriceObject);
  }, [pricesConfirmed]);

  const updateWarnings = useCallback((fuelType, priceType, value) => {
    // Check for potential bad prices
    const oldPrice = existingPrices[priceType][fuelType];
    if (oldPrice) {
      const priceDifference = parseFloat(value) - oldPrice;
      const newPriceWarnings = { ...priceWarnings };
      if (Math.abs(priceDifference) >= (station.address.country !== 'US' ? 40.0 : 0.4)) {
        newPriceWarnings[priceType][fuelType] = `This price seems ${priceDifference > 0 ? 'high' : 'low'}`;
      } else {
        delete newPriceWarnings[priceType][fuelType];
      }
      setPriceWarnings(newPriceWarnings);
    }
  }, [existingPrices, priceWarnings, station.address.country]);

  const onPriceConfirmed = useCallback((fuelType, priceType, value) => {
    updateConfirmedPrices(fuelType, priceType, value);
    updateWarnings(fuelType, priceType, value);
  }, [updateConfirmedPrices, updateWarnings]);

  const confirmedPricesCount = useMemo(() => {
    let confirmedPrices = 0;
    Object.entries(pricesConfirmed).forEach(([, value]) => {
      Object.entries(value).forEach(([, innerValue]) => {
        if (parseFloat(innerValue) > 0) {
          confirmedPrices += 1;
        }
      });
    });
    return confirmedPrices;
  }, [pricesConfirmed]);

  const hasPriceWarnings = useMemo(() => (
    Object.entries(priceWarnings).some(([, val]) => Object.entries(val).some(([, innerVal]) => innerVal))
  ), [priceWarnings]);

  const renderedModalActions = useMemo(() => {
    const thisText = pluralizeIfNeeded(confirmedPricesCount, 'this', 'ese', 2);
    const priceText = pluralizeIfNeeded(confirmedPricesCount, 'price', 's');
    const readyToSubmitText = `Submit ${confirmedPricesCount} ${priceText}`;
    const warningsText = `Submit ${thisText} ${priceText}`;
    return (
      <React.Fragment>
        {hasPriceWarnings && (
          <Text as="p" centered className={cx('warningMessage')} color="orange">
            {`Are you sure ${thisText} ${priceText} ${pluralizeIfNeeded(confirmedPricesCount, 'is', 'are', '2')} correct?`}
          </Text>
        )}
        <Button
          fluid
          primary
          warning={hasPriceWarnings}
          disabled={confirmedPricesCount === 0 || isPosting}
          loading={isPosting}
          onClick={handlePricesSubmitted}
        >
          {hasPriceWarnings ? warningsText : readyToSubmitText}
        </Button>
      </React.Fragment>
    );
  }, [confirmedPricesCount, hasPriceWarnings, handlePricesSubmitted, isPosting]);

  const renderedReportButton = useMemo(() => (
    <Button
      className={cx('reportButton')}
      primary
      size="lg"
      fluid
      onClick={openModal}
    >
      Report Prices
    </Button>
  ), [openModal]);

  function renderPriceEditor(fuelType, priceReport, pricesToRender, showPriceBadge) {
    const { isCash, price: defaultValue } = priceReport;
    const priceType = isCash ? 'cash' : 'credit';
    const editorKey = `${fuelType}_${priceType}`;
    const valuesByPriceType = pricesToRender[priceType];
    const fuelPriceValue = valuesByPriceType && valuesByPriceType[fuelType];
    const validatedDefaultValue = defaultValue > 0 ? defaultValue.toFixed(isUSStation ? 2 : 1) : '';
    const editorValue = defaultValue === undefined ? fuelPriceValue : validatedDefaultValue;
    let pricePlaceholder = defaultValue.toFixed(isUSStation ? 2 : 1);
    if (!defaultValue) {
      if (isUSStation) {
        pricePlaceholder = '0.00';
      } else {
        pricePlaceholder = '00.0';
      }
    }

    return (
      <GasPriceEditor
        key={editorKey}
        error={priceWarnings[priceType][fuelType]}
        inputId={`${editorKey}_input`}
        isConfirmed={!!valuesByPriceType[fuelType]}
        isCash={isCash}
        isInDollars={isUSStation}
        onConfirm={(v) => { onPriceConfirmed(fuelType, priceType, v); }}
        placeholder={pricePlaceholder}
        showBadge={showPriceBadge}
        value={editorValue}
      />
    );
  }

  function renderFuel(fuel, pricesToRender) {
    const { displayName, fuelType, prices } = fuel;
    const hasBothPrices = prices.length === 2;

    return (
      <React.Fragment key={`${displayName} section`}>
        <FlexGrid.Column className={cx('fuelHeaderContainer')}>
          <Header as="h3" className={cx('priceReportSubHeader')}>
            {displayName}
          </Header>
        </FlexGrid.Column>
        <FlexGrid.Column
          className={cx('fuelTypeContainer')}
          tablet={hasBothPrices ? 6 : 12}
        >
          {renderPriceEditor(fuelType, prices[0], pricesToRender, hasBothPrices)}
        </FlexGrid.Column>
        {hasBothPrices && (
          <FlexGrid.Column
            className={cx('fuelTypeContainer')}
            tablet={6}
          >
            {renderPriceEditor(fuelType, prices[1], pricesToRender, hasBothPrices)}
          </FlexGrid.Column>
        )}
      </React.Fragment>
    );
  }

  function renderPriceGrid() {
    const displayableFuels = getDisplayableFuels(fuels);
    return (
      <React.Fragment>
        <StationInfoBox
          stationId={station.id?.toString()}
          noContainer
          noRating
          basic
        />
        <Divider />
        <div className={cx('scrollableContent')}>
          <div className={cx({ centeredContent: !hasBothPriceTypes })}>
            <div className={cx('timeSpottedContainer')}>
              <Header as="h3" className={cx('priceReportSubHeader')}>
                Time Spotted
                <Tooltip
                  bordered={false}
                  className={cx('tooltip')}
                  icon={faQuestionCircle}
                  content={
                    (
                      <React.Fragment>
                        <Text as="p" bold>What is &quot;Time Spotted&quot;?</Text>
                        <Text as="p">
                          Sometimes you won&rsquo;t be able to update a station&rsquo;s price right when you see it.
                          <br /><br />
                          We ask that you use Time Spotted to make sure &quot;old&quot; prices don&rsquo;t get submitted overtop of &quot;new&quot; prices.
                        </Text>
                      </React.Fragment>
                    )
                  }
                />
              </Header>
              <Dropdown
                className={cx('timeSpottedDropdown')}
                inline
                options={TIME_SPOTTED_LIST}
                onChange={handleTimeSpottedChanged}
                upward
                value={timeSpotted}
              />
            </div>
            {!!fuels.length && (
              <FlexGrid>
                {displayableFuels.map(fuel => renderFuel(fuel, pricesConfirmed))}
              </FlexGrid>
            )}
          </div>
        </div>
        <Divider className={cx('bottomDivider')} />
      </React.Fragment>
    );
  }

  const handleMembernameModalClose = useCallback(() => {
    setIsUsernameModalOpen(false);
    setIsMainModalOpen(true);
  }, []);

  const handleUsernameUpdateFinish = useCallback(() => {
    // Username updated, so log the event before we finish up from the modal closing
    localyticsEvent(ANALYTICS_EVENTS.USERNAME_UPDATED, {
      Context: 'Price_Report',
    });

    // We need to post their prices and open the main modal again to show the thank you message
    handlePricesPosted();
    handleMembernameModalClose();
  }, [handleMembernameModalClose, handlePricesPosted]);

  if (isUsernameModalOpen) {
    return (
      <React.Fragment>
        <EditMembernameModal
          onCloseModal={handleMembernameModalClose}
          onFinish={handleUsernameUpdateFinish}
          showModal={isUsernameModalOpen}
          statusVariant={USERNAME_STATE.EDITABLE}
          memberName={memberName}
        />
        {renderedReportButton}
      </React.Fragment>
    );
  }

  return isLoggedIn
    ? (
      <Modal
        size={isReportingPrices ? 'md' : 'sm'}
        forceIsShowing={isMainModalOpen}
        onClose={closeModal}
        content={() => {
          if (isReportingPrices) {
            return (
              <React.Fragment>
                {renderPriceGrid()}
                {!!postError && (
                  <Text as="p" centered color="orange">
                    {postError}
                  </Text>
                )}
                {renderedModalActions}
              </React.Fragment>
            );
          }

          return (
            <ThankYou
              username={memberName}
              messageType="price"
              onDone={handleThankYouModalDone}
              onEditUserName={handleEditMemberName}
              isGeneratedUsername={canSetMemberName}
              pointsAwarded={pointsAwarded}
            />
          );
        }}
      >
        {renderedReportButton}
      </Modal>
    )
    : (
      <Button
        className={cx('reportButton')}
        primary
        size="lg"
        fluid
        as="a"
        href="/login"
      >
        Log In to Report Prices
      </Button>
    );
}

StationPriceReport.propTypes = {
  /** Whether the user has a generated member name */
  canSetMemberName: PropTypes.bool,
  /** An array of fuel types and prices reported at the given station */
  fuels: PropTypes.arrayOf(FuelPropType),
  /** A boolean to determine if the user successfully submitted prices */
  fuelsPosted: PropTypes.bool,
  /** A boolean determining whether the current user is logged in */
  isLoggedIn: PropTypes.bool.isRequired,
  /** A boolean to determine if there is an outstanding request to update prices */
  isPosting: PropTypes.bool,
  /** The current member name of the user */
  memberName: PropTypes.string.isRequired,
  /** A method executed when user closes the price report modal to reset fuel report posting status */
  resetPriceReportStatus: PropTypes.func,
  /** A method executed once a price report has been posted and the modal has been closed */
  onReportDone: PropTypes.func,
  /** The number of points awarded to the on successful price submission */
  pointsAwarded: PropTypes.number,
  /** An error that occurred when trying to submit prices */
  postError: PropTypes.string,
  /** The station being updated */
  station: StationPropTypes,
  /** A method executed when the user submits a report */
  submitPriceReport: PropTypes.func,
};

StationPriceReport.defaultProps = {
  canSetMemberName: false,
  fuels: [],
  fuelsPosted: false,
  isPosting: false,
  onReportDone: noop,
  pointsAwarded: undefined,
  postError: '',
  resetPriceReportStatus: noop,
  station: {
    id: 0,
    name: '',
    numRatings: undefined,
    enterprise: false,
    address: {
      line_1: '',
      line_2: '',
      locality: '',
      region: '',
      country: '',
      postal_code: '',
      cross_street: '',
      cross_street_alias: '',
      address_alias: '',
      at_intersection: false,
      nearby_street: '',
      description: '',
    },
    phone: '',
  },
  submitPriceReport: noop,
};
